import { Component, OnInit, OnDestroy, inject, model, output, effect, ChangeDetectionStrategy } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable, Subscription } from 'rxjs';
import { ExistingFormElement, QuestionView, QuestionType, QuestionTypeMapper, FormElement, FormStore } from '@formrails/form-builder/data-access';
import { DrawerResizeDirective } from '@formrails/shared/ui';
import { QuestionContainerComponent } from '../questions';

// Re-export FormElement for backward compatibility (now exported from @formrails/form-builder/data-access)
export type { FormElement };

/**
 * Palette component for selecting form elements.
 * Uses Angular signals for inputs and outputs.
 */
@Component({
  selector: 'app-form-element-palette',
  standalone: true,
  imports: [FormsModule, QuestionContainerComponent, AsyncPipe, DrawerResizeDirective],
  templateUrl: './form-element-palette.component.html',
  styleUrl: './form-element-palette.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormElementPaletteComponent implements OnInit, OnDestroy {
  // Two-way binding for visibility
  readonly visible = model(false);
  readonly elementSelected = output<FormElement>();
  /** Emits when the palette is closed without selecting an element */
  readonly cancelled = output<void>();

  private formStore = inject(FormStore);
  private subscriptions = new Subscription();

  constructor() {
    // Effect to handle visibility changes
    effect(() => {
      const isVisible = this.visible();
      // Reset when palette is closed
      if (!isVisible) {
        this.reset();
      }
    });
  }

  activeTab: 'question' | 'structural' | 'existing' = 'question';
  selectedElement: FormElement | null = null;

  // Search and filter properties
  searchText: string = '';
  selectedTypeFilter: QuestionType | 'all' = 'all';

  // Reportable toggle for new form elements
  isReportable: boolean = false;

  existingFormElements$: Observable<ExistingFormElement[]> = toObservable(this.formStore.existingFormElements);
  loadingExistingElements$: Observable<boolean> = toObservable(this.formStore.loadingExistingElements);
  existingElementsError$: Observable<string | null> = toObservable(this.formStore.existingElementsError);

  // Both lists are kept in alphabetical order by name: the palette is scanned
  // for a type by its name, so keep new entries in place rather than appended.
  questionElements: FormElement[] = [
    { id: 'checkbox', name: 'Checkbox', icon: 'pi-check-square', type: 'question' },
    { id: 'confirmation', name: 'Confirmation', icon: 'rule', iconType: 'material', type: 'question' },
    { id: 'date', name: 'Date', icon: 'pi-calendar', type: 'question' },
    { id: 'dropdown', name: 'Dropdown', icon: 'pi-chevron-down', type: 'question' },
    { id: 'email', name: 'Email', icon: 'pi-at', type: 'question' },
    { id: 'radio', name: 'Radio', icon: 'pi-circle', type: 'question' },
    { id: 'tel', name: 'Tel', icon: 'pi-phone', type: 'question' },
    { id: 'textarea', name: 'Textarea', icon: 'subject', iconType: 'material', type: 'question' },
    { id: 'textbox', name: 'Textbox', icon: 'short_text', iconType: 'material', type: 'question' },
    { id: 'toggle', name: 'Toggle', icon: 'toggle_on', iconType: 'material', type: 'question' }
  ];

  structuralElements: FormElement[] = [
    { id: 'helpertext', name: 'Helper Text', icon: 'help', iconType: 'material', type: 'structural' },
    { id: 'section', name: 'Section', icon: 'border_horizontal', iconType: 'material', type: 'structural' },
    { id: 'title', name: 'Title', icon: 'title', iconType: 'material', type: 'structural' }
  ];

  existingElements: FormElement[] = [];

  ngOnInit(): void {
    // Subscribe to existing form elements and map them to FormElement format
    this.subscriptions.add(
      this.existingFormElements$.subscribe(elements => {
        this.existingElements = this.mapToFormElements(elements);
      })
    );

    // Load existing form elements when component initializes.
    // Read the signal synchronously to avoid unnecessary API calls when cached.
    if (this.formStore.existingFormElements().length === 0) {
      this.formStore.loadExistingFormElements();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /**
   * Map ExistingFormElement models to FormElement[] format for display
   */
  private mapToFormElements(elements: ExistingFormElement[]): FormElement[] {
    return elements.map(element => {
      // Map labelText from API (preferred), with fallback to questionText for backward compatibility
      // This will later be mapped to labelText in QuestionView
      const labelText = element.labelText || 
                        element.questionText || // Backward compatibility
                        element['text'] || 
                        element['label'] || 
                        element['question'] ||
                        element.name;
      
      return {
        id: element.id,
        name: element.name,
        icon: this.getIconForType(element.type) || 'pi-file',
        iconType: this.getIconTypeForType(element.type),
        type: 'existing' as const,
        labelText: labelText, // Maps to labelText in QuestionView
        existingElementData: element // Store full data for preview rendering
      };
    });
  }

  /**
   * Convert existing form element to QuestionView for preview
   */
  private convertToQuestionView(element: FormElement): QuestionView | null {
    if (!element.existingElementData) {
      console.warn('No existingElementData found for element:', element);
      return null;
    }

    const existingData = element.existingElementData;
    const questionType = this.mapApiTypeToQuestionType(existingData.type);
    
    if (!questionType) {
      console.warn('Could not map API type to QuestionType. API type:', existingData.type);
      return null;
    }

    // Map labelText from ExistingFormElement to labelText in QuestionView
    // Priority: labelText property from API (preferred), with fallback to questionText for backward compatibility
    const labelText = existingData.labelText || 
                      existingData.questionText || // Backward compatibility
                      element.labelText ||
                      existingData['text'] || 
                      existingData['label'] || 
                      existingData['question'] ||
                      element.name ||
                      'Untitled Question';

    // Extract options from various possible property names
    // Check multiple possible locations for options array
    const options = existingData['options'] || 
                    existingData['optionValues'] ||
                    existingData['choices'] ||
                    existingData['items'] ||
                    existingData['optionList'] ||
                    [];

    // Ensure options is an array and convert to string array if needed
    let optionsArray: string[] = [];
    if (Array.isArray(options)) {
      // Convert each option to string (handles both string and object options)
      optionsArray = options.map(opt => {
        if (typeof opt === 'string') {
          return opt;
        } else if (typeof opt === 'object' && opt !== null) {
          // Handle object options - try common property names
          return opt['value'] || opt['text'] || opt['label'] || opt['name'] || String(opt);
        } else {
          return String(opt);
        }
      }).filter(opt => opt && opt.trim() !== ''); // Filter out empty options
    }

    // For HelperText type, content goes in labelText (not helperText)
    // For other types, helperText is separate from labelText
    let helperTextValue: string | undefined;
    let labelTextValue = labelText;
    
    if (questionType === QuestionType.HelperText) {
      // For HelperText, map all text content to labelText, not helperText
      labelTextValue = existingData['labelText'] || 
                       existingData['helperText'] || 
                       existingData['questionText'] ||
                       existingData['text'] ||
                       existingData['label'] ||
                       existingData['question'] ||
                       labelText ||
                       'Helper Text';
      helperTextValue = undefined; // HelperText type doesn't use helperText attribute
    } else {
      helperTextValue = existingData['helperText'] || '';
    }

    const questionView: QuestionView = {
      id: element.id,
      labelText: labelTextValue,
      type: existingData.type || '',
      isRequired: existingData['isRequired'] === true || existingData['required'] === true || existingData['required'] === 'true',
      questionType: questionType,
      order: 0,
      showMenu: false,
      isEditing: false,
      questionNumber: undefined,
      sectionId: undefined,
      additionalInfo: existingData['additionalInfo'] || '',
      validators: existingData['validators'] || {},
      tooltip: existingData['tooltip'] || '',
      helperText: helperTextValue,
      placeholder: existingData['placeholder'] || '',
      semanticTags: existingData['semanticTags'] || [],
      options: optionsArray, // Use extracted options array
      isSearchable: existingData['isSearchable'] || false,
      deleted: existingData['deleted'] || false,
      lastUpdatedOn: existingData['lastUpdatedOn'] || '0001-01-01T00:00:00',
      lastUpdatedBy: existingData['lastUpdatedBy'] || null
    };

    return questionView;
  }

  /**
   * Map API type string to QuestionType enum
   */
  private mapApiTypeToQuestionType(apiType: string): QuestionType | null {
    return QuestionTypeMapper.mapApiTypeToQuestionType(apiType);
  }
  /**
   * Get QuestionView for preview if selected element is existing
   */
  getPreviewQuestion(): QuestionView | null {
    if (!this.selectedElement || this.selectedElement.type !== 'existing') {
      return null;
    }
    
    const questionView = this.convertToQuestionView(this.selectedElement);
    
    // Debug logging (remove in production if needed)
    if (!questionView) {
      console.warn('Failed to convert existing element to QuestionView:', this.selectedElement);
    }
    
    return questionView;
  }

  /**
   * Check if a question is a dropdown question type
   */
  isDropdownQuestion(question: QuestionView): boolean {
    return question.questionType === QuestionType.DropdownList;
  }

  /**
   * Get appropriate icon based on element type
   */
  private getIconForType(type: string): string {
    const iconMap: Record<string, string> = {
      'checkbox': 'pi-check-square',
      'dropdown': 'pi-chevron-down',
      'dynamicdropdown': 'dynamic_feed',
      'dynamic-dropdown': 'dynamic_feed',
      'email': 'pi-at',
      'date': 'pi-calendar',
      'input': 'short_text',
      'textbox': 'short_text',
      'tel': 'pi-phone',
      'phone': 'pi-phone', // Backward compatibility
      'radio': 'pi-circle',
      'textarea': 'subject',
      'toggle': 'toggle_on',
      'confirmation': 'rule',
      'helpertext': 'help',
      'helper-text': 'help', // Backward compatibility
      'helper': 'help', // Alternative format
      'section': 'border_horizontal',
      'title': 'title'
    };

    const lowerType = type.toLowerCase();
    return iconMap[lowerType] || 'pi-file';
  }

  /**
   * Get icon type (prime or material) based on element type
   */
  private getIconTypeForType(type: string): 'prime' | 'material' | undefined {
    const materialTypes = ['input', 'textbox', 'textarea', 'toggle', 'confirmation', 'helpertext', 'section', 'title', 'dynamicdropdown', 'dynamic-dropdown'];
    const lowerType = type.toLowerCase();
    return materialTypes.some(t => lowerType.includes(t)) ? 'material' : undefined;
  }

  get currentElements(): FormElement[] {
    switch (this.activeTab) {
      case 'question':
        return this.questionElements;
      case 'structural':
        return this.structuralElements;
      case 'existing':
        return this.filteredExistingElements;
      default:
        return this.questionElements;
    }
  }

  /**
   * Get filtered existing elements based on search text and type filter
   */
  get filteredExistingElements(): FormElement[] {
    let filtered = this.existingElements;

    // Filter by search text (labelText)
    if (this.searchText.trim()) {
      const searchLower = this.searchText.toLowerCase().trim();
      filtered = filtered.filter(element => {
        const labelText = element.labelText || element.name || '';
        return labelText.toLowerCase().includes(searchLower);
      });
    }

    // Filter by question type
    if (this.selectedTypeFilter !== 'all') {
      filtered = filtered.filter(element => {
        if (!element.existingElementData) {
          return false;
        }
        const elementQuestionType = this.mapApiTypeToQuestionType(element.existingElementData.type);
        return elementQuestionType === this.selectedTypeFilter;
      });
    }

    return filtered;
  }

  /**
   * Get available question types for filter
   * Ordered alphabetically by label, with "All Types" first
   */
  get availableQuestionTypes(): Array<{ value: QuestionType | 'all'; label: string }> {
    const types = [
      { value: QuestionType.CheckboxList, label: 'Checkbox' },
      { value: QuestionType.Confirmation, label: 'Confirmation' },
      { value: QuestionType.DropdownList, label: 'Dropdown' },
      { value: QuestionType.DynamicDropdown, label: 'Dynamic Dropdown' },
      { value: QuestionType.Date, label: 'Date' },
      { value: QuestionType.Email, label: 'Email' },
      { value: QuestionType.HelperText, label: 'Helper Text' },
      { value: QuestionType.Tel, label: 'Tel' },
      { value: QuestionType.Radio, label: 'Radio' },
      { value: QuestionType.TextArea, label: 'Textarea' },
      { value: QuestionType.TextBox, label: 'Textbox' },
      { value: QuestionType.Toggle, label: 'Toggle' }
    ];
    
    // Sort alphabetically by label
    types.sort((a, b) => a.label.localeCompare(b.label));
    
    // Return with "All Types" first, then sorted types
    return [
      { value: 'all', label: 'All Types' },
      ...types
    ];
  }

  selectTab(tab: 'question' | 'structural' | 'existing'): void {
    this.activeTab = tab;
    // Clear selection when switching tabs
    this.selectedElement = null;
    
    // Reset search and filter when switching away from existing tab
    if (tab !== 'existing') {
      this.searchText = '';
      this.selectedTypeFilter = 'all';
    }
    
    // Load existing form elements when switching to existing tab if not already loaded
    if (tab === 'existing' && this.existingElements.length === 0) {
      this.formStore.loadExistingFormElements();
    }
  }

  selectElementForPreview(element: FormElement): void {
    this.selectedElement = element;
  }

  confirmSelection(): void {
    if (this.selectedElement) {
      console.log('Palette: Confirming selection of element:', this.selectedElement);
      // Create a copy and include reportable flag for new elements
      const elementToEmit = {
        ...this.selectedElement,
        // Only set isReportable for new elements (question/structural tabs, not existing)
        isReportable: this.selectedElement.type !== 'existing' ? this.isReportable : false,
        // Mark as new element if not from existing library
        isNewElement: this.selectedElement.type !== 'existing'
      };
      this.elementSelected.emit(elementToEmit);
      // Reset after emitting (but before closing to ensure event is processed)
      this.reset();
      // Close the palette after selection
      this.close();
    } else {
      console.warn('Palette: No element selected to confirm');
    }
  }

  reset(): void {
    this.selectedElement = null;
    this.activeTab = 'question';
    this.searchText = '';
    this.selectedTypeFilter = 'all';
    this.isReportable = false;
  }

  /**
   * Close the palette without selecting an element.
   * Emits the cancelled event so the parent can clean up any placeholder questions.
   */
  close(): void {
    this.cancelled.emit();
    this.visible.set(false);
    this.reset();
  }

  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('palette-backdrop')) {
      this.close();
    }
  }
}

